import { graphql } from 'gql.tada'

/**
 * SALESMAN project projection (#321 FR-007 + #326), expressed as a fixed
 * gql.tada selection.
 *
 * Replaces the former Refine `MetaQuery` field-list (`useProjectsMetadata`)
 * that master's wave-5 Refine cleanup removed. Defense-in-depth — the
 * salesman client never requests forbidden columns (corporate_name,
 * additional_signatory_clients). The `urban_analysis` object relation is
 * intentionally selected for the étape 1/3 "Rappel étude urbaine" notice
 * (#364, detail document) and for the projects-list card's urban-analysis
 * chip (Project Hub – Improved). It is covered by the Hasura SALESMAN
 * `select_permissions` on `urban_analysis` (own-project rows only,
 * `id`/`status`/`requestor` columns). `requestor` gates whether the salesman
 * may treat the study as available (only when the salesman side ordered it —
 * see `isUrbanAnalysisRequestedBySalesmanSide`). Every
 * other field below is covered by the Hasura SALESMAN `select_permissions`
 * on the respective table; the curated `projectHubFieldSubsets/*` services
 * drive the inline editor from exactly this shape.
 *
 * The selection is inlined in both the list and detail documents (rather
 * than shared via a fragment) to mirror `features/projects/services/
 * projects.graphql.ts` and keep the consuming hooks' types flat.
 */

export const HubProjectsTableDocument = graphql(`
  query HubProjectsTable(
    $where: projects_bool_exp
    $order_by: [projects_order_by!]
  ) {
    # Unpaginated on purpose: the page groups its results into À faire / En
    # attente / Terminés sections, and a page window cut across those groups —
    # "3 à faire" in the chip with one card under the heading, because the other
    # two sat on page 2. The chip counts are whole-book aggregates, so the only
    # way they can agree with what is rendered is to render the whole book.
    projects(where: $where, order_by: $order_by) {
      id
      step
      created
      project_type
      planning_file_type
      # Section statuses that live on the projects row itself rather than on a
      # form row. Together with the nested status fields below they are what
      # lets the list say "2 rubriques à corriger" without asking the
      # per-project status action — see list/projectSituation.ts.
      land_status
      declarers_status
      # Usage + status only. Enough to count what the architect rejected; NOT
      # enough to recompute the required-document shortfall, which is
      # deliberately left to the project page's authoritative status.
      files {
        use
        status
      }
      # When the dossier entered the step it is in. These are the SAME
      # milestone columns the architect's dashboard_project_cycle_times view
      # reads (created / validated / started_at / file_completed_at /
      # *_sending_date / *_deposite_date), so both sides date a step from one
      # source. See stepEnteredAt in list/projectSituation.ts.
      requested
      validated
      ordered
      paid
      started_at
      file_completed_at
      closed
      # Who lodges a FILE_SENT dossier (F2c): the commercial's deposit is
      # "À faire", the architect's online filing is "En attente".
      deposit_mode
      # Ordering still runs on the generic activity aggregate — order_by
      # cannot pick a different column per row, and the milestone that matters
      # depends on the step. See the note on ORDER_BY_FOR_SORT.
      events_aggregate {
        aggregate {
          max {
            created
          }
        }
      }
      primary_signatory_client {
        id
        first_name
        last_name
        gender
        birth_date
        birth_place
        birth_postal_code
        birth_country
        email_address
        phone
        mobile_phone
        street_number
        street_name
        postal_code
        city
      }
      street_number
      street_name
      named_place
      postal_code
      city
      existing_building {
        id
        status
        comment
        primary_residence
        secondary_residence
        allotment_min10
        basement_without_garage_area
        rdc_area
        first_floor_area
        second_floor_area
        attic_livable_area
        underground_garage_area
        outdoor_garage_area
        barn_area
        warehouse_area
        workshot_area
        hut_area
      }
      veranda {
        id
        status
        comment
        area
        width
        length
        height_below_hook
        height_below_sewer
        frosted_glass
        little_wood
        other_info
        veranda_materials {
          veranda_material_id
        }
        veranda_colors {
          veranda_color_id
        }
        roof_materials {
          roof_material_id
        }
        base_panel_materials {
          base_panel_material_id
        }
      }
      pergola {
        id
        status
        comment
        area
        width
        length
        height_below_hook
        height_below_sewer
        bioclimatic
        frosted_glass
        other_info
        pergola_materials {
          pergola_material_id
        }
        pergola_colors {
          pergola_color_id
        }
        roof_materials {
          roof_material_id
        }
        base_panel_materials {
          base_panel_material_id
        }
      }
      pool {
        id
        status
        comment
        area
        width
        length
        height_min
        height_max
        has_liner
        has_margel
        has_cover
        other_info
        liner_colors {
          liner_color_id
        }
        margel_colors {
          margel_color_id
        }
        cover_types {
          cover_type_id
        }
        cover_colors {
          cover_color_id
        }
      }
      last_sending_date
      last_deposite_date
      validated
      ordered
      architect {
        first_name
        last_name
        phone
      }
      urban_analysis {
        id
        status
        requestor
      }
      salesman {
        id
        first_name
        last_name
      }
    }
    projects_aggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
`)

export const HubProjectDetailDocument = graphql(`
  query HubProjectDetail($id: Int!) {
    projects_by_pk(id: $id) {
      id
      step
      created
      project_type
      planning_file_type
      reserves
      refused_by
      primary_signatory_client {
        id
        first_name
        last_name
        gender
        birth_date
        birth_place
        birth_postal_code
        birth_country
        email_address
        phone
        mobile_phone
        street_number
        street_name
        postal_code
        city
      }
      street_number
      street_name
      named_place
      postal_code
      city
      existing_building {
        id
        status
        comment
        primary_residence
        secondary_residence
        allotment_min10
        basement_without_garage_area
        rdc_area
        first_floor_area
        second_floor_area
        attic_livable_area
        underground_garage_area
        outdoor_garage_area
        barn_area
        warehouse_area
        workshot_area
        hut_area
      }
      veranda {
        id
        status
        comment
        area
        width
        length
        height_below_hook
        height_below_sewer
        frosted_glass
        little_wood
        other_info
        veranda_materials {
          veranda_material_id
        }
        veranda_colors {
          veranda_color_id
        }
        roof_materials {
          roof_material_id
        }
        base_panel_materials {
          base_panel_material_id
        }
      }
      pergola {
        id
        status
        comment
        area
        width
        length
        height_below_hook
        height_below_sewer
        bioclimatic
        frosted_glass
        other_info
        pergola_materials {
          pergola_material_id
        }
        pergola_colors {
          pergola_color_id
        }
        roof_materials {
          roof_material_id
        }
        base_panel_materials {
          base_panel_material_id
        }
      }
      pool {
        id
        status
        comment
        area
        width
        length
        height_min
        height_max
        has_liner
        has_margel
        has_cover
        other_info
        liner_colors {
          liner_color_id
        }
        margel_colors {
          margel_color_id
        }
        cover_types {
          cover_type_id
        }
        cover_colors {
          cover_color_id
        }
      }
      last_sending_date
      deposit_mode
      mairie_decision
      mairie_decision_message
      deposite_number
      first_deposite_date
      last_deposite_date
      instruction_end
      validated
      ordered
      architect {
        first_name
        last_name
        phone
      }
      urban_analysis {
        id
        status
        requestor
      }
      salesman {
        id
        first_name
        last_name
      }
    }
  }
`)
